import React, {ReactElement} from 'react';

import find from 'lodash/find';
import get from 'lodash/get';

import {gt} from 'app/utils/i18n';

import type {AnyStepType} from 'sweet-types';

type NamedObjType = {
  readonly name: string | ReactElement<'div'>;
};
interface StringNamedObjType {
  readonly name: string;
}

export type MaybeNamedObjType = string | NamedObjType | void | null;

export type ChosenNameType = {
  name: string;
  isFallback: boolean;
};
const FALLBACK_CLASSES = 'unnamed font-italic text-muted';

const broaderGet = (
  object: MaybeNamedObjType,
  path: Array<string> | string,
  defaultValue?: any
): any =>
  typeof object !== 'object' || object == null
    ? defaultValue
    : get(object, path, defaultValue);

const getFallbackForContentType = (
  contentType: string | null | void
): string => {
  switch (contentType) {
    case 'user':
      return gt('Unknown Member');

    case 'procedure':
      return gt('Untitled Procedure');

    case 'process':
    case 'proceduregroup':
      return gt('Untitled process');

    case 'policy':
      return gt('Untitled policy');

    case 'task':
    case 'taskinstance':
    case 'tasktemplate':
      return gt('Untitled Task');

    case 'step':
      return gt('Untitled Step');

    case 'quiz':
      return gt('Untitled Quiz');

    case 'answer':
      return gt('Untitled Answer');

    case 'question':
      return gt('Untitled Question');

    case 'accountprofile':
      return gt('SweetProcess account');

    case 'knowledgebase':
      return gt('Untitled Knowledge Base');
    case 'template':
      return gt('Untitled Template');

    default:
      return gt('Untitled');
  }
};

const getFirstValidName = (
  names: Array<string | null | void>
): ChosenNameType => {
  const name =
    find(names, (name) => typeof name === 'string' && name !== '') || '';
  return {
    name,
    isFallback: names.length > 0 && names[0] !== name,
  };
};

const styleName = (name: ChosenNameType): ReactElement<'span'> => (
  <span className={name.isFallback ? FALLBACK_CLASSES : ''}>{name.name}</span>
);

export const getObjectName = (
  obj: MaybeNamedObjType,
  contentType: string | null | void,
  styled = false
): ReactElement<'span'> | string => {
  const name = broaderGet(obj, 'name', obj);
  const chosenName: ChosenNameType = getFirstValidName([
    name,
    getFallbackForContentType(contentType),
  ]);
  return styled ? styleName(chosenName) : chosenName.name;
};

export const getUnstyledProcedureName = (
  obj: MaybeNamedObjType
): ReactElement | string => getProcedureName(obj, false);

export const getProcedureName = (
  obj: MaybeNamedObjType,
  styled = false
): ReactElement<'span'> | string => getObjectName(obj, 'procedure', styled);

export const getProcessName = (
  obj: MaybeNamedObjType,
  styled = false
): ReactElement | string => getObjectName(obj, 'process', styled);

export const getPolicyName = (
  obj: MaybeNamedObjType,
  styled = false
): ReactElement | string => getObjectName(obj, 'policy', styled);

export const getQuizName = (
  obj: MaybeNamedObjType,
  styled = false
): ReactElement | string => getObjectName(obj, 'quiz', styled);

export const getQuestionName = (
  obj: MaybeNamedObjType,
  styled = false
): ReactElement | string => getObjectName(obj, 'question', styled);

export const getAnswerName = (
  obj: MaybeNamedObjType,
  styled = false
): ReactElement | string => getObjectName(obj, 'answer', styled);

export const getTemplateName = (obj: StringNamedObjType): string =>
  getObjectName(obj, 'template', false) as string;

export const getStepName = <
  T extends {
    title?: string;
    type?:
      | 'content'
      | 'decision'
      | 'ending'
      | 'processprocedure'
      | 'automation';
  }
>(
  step: T,
  styled = false
): ReactElement | string =>
  getObjectName(
    step.title,
    step.type === 'processprocedure' ? 'procedure' : 'step',
    styled
  );

export function renderStepName(
  step: AnyStepType,
  renderName: (title: string, untitled: boolean) => JSX.Element
): JSX.Element {
  if (step.type === 'processprocedure') {
    if (step.deleted) {
      return renderName(gt('Deleted Procedure'), true);
    }
    if (!step.title) {
      return renderName(gt('Untitled Procedure'), true);
    }
  } else if (step.type === 'decision' && !step.title) {
    return renderName(gt('Untitled decision'), true);
  } else if (!step.title) {
    return renderName(gt('Untitled step'), true);
  }

  return renderName(step.title, false);
}

export function StepName({
  step,
  untitledClass = 'text-black-50 font-italic',
}: {
  step: AnyStepType;
  untitledClass?: string;
}): JSX.Element {
  return renderStepName(step, (name, untitled) => (
    <span className={untitled ? untitledClass : ''}>{name}</span>
  ));
}
